import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Key,
  CheckCircle,
  XCircle,
  Eye,
  EyeSlash,
  Lock,
  Password,
  ArrowsClockwise,
  Plugs,
  Tag,
  Trash,
  PencilSimple,
  FloppyDisk,
  X as CloseIcon,
  CaretDown,
} from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, getAuthHeaders, isSuperAdmin: isSuperAdminCtx } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showKeys, setShowKeys] = useState({ outscraper: false, millionverifier: false });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [editName, setEditName] = useState("");
  const [savingName, setSavingName] = useState(false);
  const [formData, setFormData] = useState({
    outscraper_api_key: "",
    millionverifier_api_key: ""
  });
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });
  const [categoryTags, setCategoryTags] = useState([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [editingTag, setEditingTag] = useState(null); // { id, name, categories: [] } while editing
  const [savingTag, setSavingTag] = useState(false);

  // GHL CRM Settings state
  const [ghlSettings, setGhlSettings] = useState({ pipeline_id: "", stage_mapping: {} });
  const [ghlPipelines, setGhlPipelines] = useState([]);
  const [ghlStages, setGhlStages] = useState([]);
  const [ghlTesting, setGhlTesting] = useState(false);
  const [ghlTestResult, setGhlTestResult] = useState(null);
  const [ghlSaving, setGhlSaving] = useState(false);
  const [ghlLoadingPipelines, setGhlLoadingPipelines] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const isSuperAdmin = isSuperAdminCtx;

  useEffect(() => {
    fetchSettings();
    fetchCategoryTags();
    if (isAdmin) {
      fetchGhlSettings();
      fetchGhlPipelines();
    }
  }, []);

  const fetchCategoryTags = async () => {
    setTagsLoading(true);
    try {
      const res = await axios.get(`${API}/category-tags`, { headers: getAuthHeaders() });
      // Settings manages only the current user's tags. Shared tags live in the
      // New Job modal; users can't edit/delete other people's tags anyway.
      setCategoryTags((res.data || []).filter(t => t.is_owner));
    } catch {
      // non-critical
    } finally {
      setTagsLoading(false);
    }
  };

  const handleDeleteTag = async (tag) => {
    if (!window.confirm(`Delete tag "${tag.name}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/category-tags/${tag.id}`, { headers: getAuthHeaders() });
      setCategoryTags(prev => prev.filter(t => t.id !== tag.id));
      toast.success(`Deleted "${tag.name}"`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete tag");
    }
  };

  const handleSaveTagEdit = async () => {
    if (!editingTag) return;
    const newName = editingTag.name.trim();
    if (!newName) {
      toast.error("Tag name cannot be empty");
      return;
    }
    setSavingTag(true);
    try {
      const res = await axios.put(
        `${API}/category-tags/${editingTag.id}`,
        { name: newName, categories: editingTag.categories },
        { headers: getAuthHeaders() }
      );
      setCategoryTags(prev => prev.map(t => t.id === res.data.id ? res.data : t)
                                  .sort((a, b) => a.name.localeCompare(b.name)));
      setEditingTag(null);
      toast.success("Tag updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update tag");
    } finally {
      setSavingTag(false);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`, {
        headers: getAuthHeaders()
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGhlSettings = async () => {
    try {
      const res = await axios.get(`${API}/integrations/ghl/settings`, { headers: getAuthHeaders() });
      setGhlSettings(res.data);
      if (res.data.pipeline_id) fetchGhlStages(res.data.pipeline_id);
    } catch {}
  };

  const fetchGhlPipelines = async () => {
    setGhlLoadingPipelines(true);
    try {
      const res = await axios.get(`${API}/integrations/ghl/pipelines`, { headers: getAuthHeaders() });
      setGhlPipelines(res.data);
    } catch {}
    setGhlLoadingPipelines(false);
  };

  const fetchGhlStages = async (pipelineId) => {
    if (!pipelineId) { setGhlStages([]); return; }
    try {
      const res = await axios.get(`${API}/integrations/ghl/pipelines/${pipelineId}/stages`, { headers: getAuthHeaders() });
      setGhlStages(res.data);
    } catch {}
  };

  const handleGhlPipelineChange = (pipelineId) => {
    setGhlSettings(prev => ({ ...prev, pipeline_id: pipelineId, stage_mapping: {} }));
    fetchGhlStages(pipelineId);
  };

  const handleGhlStageMapping = (key, stageId) => {
    setGhlSettings(prev => ({
      ...prev,
      stage_mapping: { ...prev.stage_mapping, [key]: stageId }
    }));
  };

  const handleGhlTest = async () => {
    setGhlTesting(true);
    setGhlTestResult(null);
    try {
      const res = await axios.get(`${API}/integrations/ghl/test`, { headers: getAuthHeaders() });
      setGhlTestResult({ success: true, message: res.data.message });
      toast.success("GHL connection successful");
    } catch (err) {
      setGhlTestResult({ success: false, message: err.response?.data?.detail || "Connection failed" });
      toast.error(err.response?.data?.detail || "GHL connection failed");
    }
    setGhlTesting(false);
  };

  const handleGhlSave = async () => {
    setGhlSaving(true);
    try {
      await axios.put(`${API}/integrations/ghl/settings`, {
        pipeline_id: ghlSettings.pipeline_id,
        stage_mapping: ghlSettings.stage_mapping
      }, { headers: getAuthHeaders() });
      toast.success("CRM settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save CRM settings");
    }
    setGhlSaving(false);
  };

  const handleSave = async () => {
    if (!isAdmin) {
      toast.error("Only admins can update settings");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/settings`, formData, {
        headers: getAuthHeaders()
      });
      toast.success("Settings updated successfully");
      setFormData({ outscraper_api_key: "", millionverifier_api_key: "" });
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordData.current_password || !passwordData.new_password) {
      toast.error("Please fill in all password fields");
      return;
    }
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setChangingPassword(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      }, {
        headers: getAuthHeaders()
      });
      toast.success("Password changed successfully");
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleUpdateName = async () => {
    if (!editName.trim() || editName.trim().length < 2) {
      toast.error("Name must be at least 2 characters");
      return;
    }
    setSavingName(true);
    try {
      await axios.put(`${API}/auth/update-profile`, { name: editName.trim() }, {
        headers: getAuthHeaders()
      });
      toast.success("Name updated");
      setEditName("");
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update name");
    } finally {
      setSavingName(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#006994] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
          Settings
        </h1>
        <p className="text-slate-500 mt-1 text-sm">Configure API integrations and account settings. Click any section to expand.</p>
      </div>

      {/* Profile & Password — expanded by default (most common daily task) */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="glass-card border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg text-slate-900">Profile & Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Profile Info */}
            <div className="flex items-center gap-4 p-4 rounded-lg bg-white">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-[#006994] to-[#0EA5E9] flex items-center justify-center text-white font-bold text-2xl">
                {user?.name?.charAt(0) || "U"}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {editName !== "" ? (
                    <div className="flex items-center gap-2">
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="bg-white border-slate-200 text-slate-900 h-8 w-48"
                        placeholder="Enter new name"
                        data-testid="edit-name-input"
                      />
                      <Button size="sm" onClick={handleUpdateName} disabled={savingName} className="bg-[#006994] text-white h-8 px-3 text-xs font-bold hover:bg-[#005A80]" data-testid="save-name-btn">
                        {savingName ? "..." : "Save"}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditName("")} className="text-slate-500 h-8 px-2">
                        Cancel
                      </Button>
                    </div>
                  ) : (
                    <>
                      <p className="text-lg font-medium text-slate-900">{user?.name}</p>
                      <button onClick={() => setEditName(user?.name || "")} className="text-xs text-slate-500 hover:text-[#006994] transition-colors" data-testid="edit-name-btn">
                        Edit
                      </button>
                    </>
                  )}
                </div>
                <p className="text-sm text-slate-500">{user?.email}</p>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1 ${
                  user?.role === "super_admin"
                    ? "bg-[#006994]/10 text-[#006994] border border-[#006994]/30"
                    : user?.role === "admin"
                    ? "bg-[#0EA5E9]/10 text-[#0EA5E9] border border-[#0EA5E9]/20"
                    : "bg-slate-100 text-slate-500 border border-slate-200"
                }`}>
                  {user?.role === "super_admin" ? "Super Admin" : user?.role === "admin" ? "Admin" : "User"}
                </span>
              </div>
            </div>

            {/* Change Password */}
            <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex items-center gap-2">
                <Lock size={20} className="text-[#006994]" />
                <h3 className="font-medium text-slate-900">Change Password</h3>
              </div>
              
              <div className="grid gap-4">
                <div className="space-y-2">
                  <Label className="text-slate-500 text-sm">Current Password</Label>
                  <div className="relative">
                    <Input
                      type={showPasswords.current ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                      placeholder="Enter current password"
                      className="pr-10 bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900"
                      data-testid="current-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(prev => ({ ...prev, current: !prev.current }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                    >
                      {showPasswords.current ? <EyeSlash size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-sm">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.new ? "text" : "password"}
                        value={passwordData.new_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                        placeholder="Enter new password"
                        className="pr-10 bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, new: !prev.new }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                      >
                        {showPasswords.new ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-slate-500 text-sm">Confirm New Password</Label>
                    <div className="relative">
                      <Input
                        type={showPasswords.confirm ? "text" : "password"}
                        value={passwordData.confirm_password}
                        onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                        placeholder="Confirm new password"
                        className="pr-10 bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900"
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPasswords(prev => ({ ...prev, confirm: !prev.confirm }))}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                      >
                        {showPasswords.confirm ? <EyeSlash size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>
                </div>
                
                <Button
                  onClick={handleChangePassword}
                  disabled={changingPassword || !passwordData.current_password || !passwordData.new_password}
                  className="w-full md:w-auto bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? "Changing..." : "Change Password"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* My Category Tags — collapsed by default */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
      >
       <details className="glass-card border border-slate-200 rounded-lg group overflow-hidden" data-testid="settings-details-tags">
        <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
          <div>
            <div className="text-lg text-slate-900 font-semibold flex items-center gap-2">
              <Tag size={18} className="text-[#006994]" /> My Category Tags
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Save frequently-used category lists for one-click loading on new jobs.</div>
          </div>
          <CaretDown size={18} className="text-slate-500 transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t border-slate-200 px-6 py-5">
            {tagsLoading ? (
              <p className="text-sm text-slate-500">Loading tags...</p>
            ) : categoryTags.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-slate-200 rounded-lg">
                <Tag size={32} className="text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-slate-500">No tags yet</p>
                <p className="text-xs text-slate-400 mt-1">
                  Create one from the category picker on the <span className="text-[#006994]">New Job</span> page.
                </p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="settings-tags-list">
                {categoryTags.map(tag => (
                  <div key={tag.id} className="rounded-lg border border-slate-200 bg-slate-50 overflow-hidden">
                    {editingTag?.id === tag.id ? (
                      <div className="p-4 space-y-3">
                        <Input
                          value={editingTag.name}
                          onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                          className="bg-white/50 border-slate-200 text-slate-900"
                          placeholder="Tag name"
                          data-testid={`edit-tag-name-${tag.id}`}
                        />
                        <div>
                          <p className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
                            Categories ({editingTag.categories.length}) — click a chip to remove
                          </p>
                          <div className="flex flex-wrap gap-1.5 max-h-48 overflow-y-auto p-2 rounded border border-slate-200 bg-white/30">
                            {editingTag.categories.length === 0 ? (
                              <span className="text-xs text-slate-500 italic">No categories — edit categories in New Job to re-save</span>
                            ) : editingTag.categories.map(c => (
                              <button
                                key={c}
                                type="button"
                                onClick={() => setEditingTag({
                                  ...editingTag,
                                  categories: editingTag.categories.filter(x => x !== c)
                                })}
                                className="group px-2 py-0.5 rounded-full bg-slate-100 text-xs text-slate-700 hover:bg-red-500/20 hover:text-red-300 transition-colors"
                                data-testid={`remove-cat-${c}`}
                              >
                                {c} <span className="opacity-0 group-hover:opacity-100">×</span>
                              </button>
                            ))}
                          </div>
                        </div>
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setEditingTag(null)}
                            className="text-slate-500 hover:bg-slate-100"
                          >
                            <CloseIcon size={14} className="mr-1" /> Cancel
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            onClick={handleSaveTagEdit}
                            disabled={savingTag}
                            className="bg-[#006994] hover:bg-[#005A80] text-white font-bold"
                            data-testid={`save-tag-edit-${tag.id}`}
                          >
                            <FloppyDisk size={14} className="mr-1" />
                            {savingTag ? "Saving..." : "Save"}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between p-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-slate-900">{tag.name}</span>
                            <span className="text-[11px] text-slate-500">
                              {tag.categories.length} {tag.categories.length === 1 ? "category" : "categories"}
                            </span>
                          </div>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-1">
                            {tag.categories.slice(0, 6).join(", ")}
                            {tag.categories.length > 6 && ` +${tag.categories.length - 6} more`}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 ml-3">
                          <button
                            type="button"
                            onClick={() => setEditingTag({ ...tag, categories: [...tag.categories] })}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#006994] transition-colors"
                            title="Edit"
                            data-testid={`edit-tag-${tag.id}`}
                          >
                            <PencilSimple size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteTag(tag)}
                            className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-red-400 transition-colors"
                            title="Delete"
                            data-testid={`delete-tag-${tag.id}`}
                          >
                            <Trash size={14} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
        </div>
       </details>
      </motion.div>

      {/* API Configuration - GLOBAL infra keys, super-admin only. Collapsed by default. */}
      {isSuperAdmin && (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
       <details className="glass-card border border-slate-200 rounded-lg group overflow-hidden" data-testid="settings-details-api">
        <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
          <div>
            <div className="text-lg text-slate-900 font-semibold flex items-center gap-2">
              <Key size={18} className="text-[#006994]" /> Global API Configuration
            </div>
            <div className="text-xs text-slate-500 mt-0.5">Shared LeadFlow Scraper + MillionVerifier keys across ALL organizations.</div>
          </div>
          <CaretDown size={18} className="text-slate-500 transition-transform group-open:rotate-180" />
        </summary>
        <div className="border-t border-slate-200 px-6 py-5">
        <Card className="glass-card border-slate-200 [&>div:first-child]:hidden">
          <CardHeader className="hidden">
            <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
              <Key size={20} className="text-[#006994]" />
              Global API Configuration
            </CardTitle>
            <CardDescription className="text-slate-500">
              Shared infrastructure keys used across ALL organizations. The LeadFlow Scraper powers lead scraping; MillionVerifier handles email verification.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* LeadFlow Scraper API Key */}
            <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="font-medium text-slate-900">LeadFlow Scraper API</h3>
                  {settings?.outscraper_configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
                      <CheckCircle size={12} />
                      Configured
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-500/10 text-red-400">
                      <XCircle size={12} />
                      Not Configured
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Used for scraping business leads from Google Maps.{" "}
                <a href="https://outscraper.com" target="_blank" rel="noopener noreferrer" className="text-[#006994] hover:underline">
                  Get API Key
                </a>
              </p>
              {settings?.outscraper_api_key && (
                <p className="text-sm text-slate-500 font-mono break-all">
                  Current: {showKeys.outscraper ? settings.outscraper_api_key : "•".repeat(Math.min(24, settings.outscraper_api_key.length))}
                </p>
              )}
              {user.role === "admin" || user.role === "super_admin" ? (
                <div className="relative">
                  <Input
                    type={showKeys.outscraper ? "text" : "password"}
                    value={formData.outscraper_api_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, outscraper_api_key: e.target.value }))}
                    placeholder="Enter new LeadFlow Scraper API key"
                    className="pr-10 bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900 font-mono text-sm"
                    data-testid="outscraper-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys(prev => ({ ...prev, outscraper: !prev.outscraper }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  >
                    {showKeys.outscraper ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              ) : null}
            </div>

            {/* MillionVerifier API Key */}
            <div className="space-y-4 p-4 rounded-lg border border-slate-200 bg-slate-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="font-medium text-slate-900">MillionVerifier API</h3>
                  {settings?.millionverifier_configured ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400">
                      <CheckCircle size={12} />
                      Configured
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-500/10 text-yellow-400">
                      Optional
                    </span>
                  )}
                </div>
              </div>
              <p className="text-sm text-slate-500">
                Used for email verification. Optional but recommended.{" "}
                <a href="https://millionverifier.com" target="_blank" rel="noopener noreferrer" className="text-[#006994] hover:underline">
                  Get API Key
                </a>
              </p>
              {settings?.millionverifier_api_key && (
                <p className="text-sm text-slate-500 font-mono break-all">
                  Current: {showKeys.millionverifier ? settings.millionverifier_api_key : "•".repeat(Math.min(24, settings.millionverifier_api_key.length))}
                </p>
              )}
              {user.role === "admin" || user.role === "super_admin" ? (
                <div className="relative">
                  <Input
                    type={showKeys.millionverifier ? "text" : "password"}
                    value={formData.millionverifier_api_key}
                    onChange={(e) => setFormData(prev => ({ ...prev, millionverifier_api_key: e.target.value }))}
                    placeholder="Enter MillionVerifier API key (optional)"
                    className="pr-10 bg-white/50 border-slate-200 focus:border-[#006994] text-slate-900 font-mono text-sm"
                    data-testid="millionverifier-key-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowKeys(prev => ({ ...prev, millionverifier: !prev.millionverifier }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-900"
                  >
                    {showKeys.millionverifier ? <EyeSlash size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              ) : null}
            </div>

            {(user.role === "admin" || user.role === "super_admin") && (
              <Button
                onClick={handleSave}
                disabled={saving || (!formData.outscraper_api_key && !formData.millionverifier_api_key)}
                className="w-full bg-[#006994] hover:bg-[#005A80] text-white font-bold rounded-sm "
                data-testid="save-settings-btn"
              >
                {saving ? "Saving..." : "Save API Settings"}
              </Button>
            )}
          </CardContent>
        </Card>
        </div>
       </details>
      </motion.div>
      )}

      {/* GHL CRM Settings — collapsed by default */}
      {isAdmin && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
         <details className="glass-card border border-slate-200 rounded-lg group overflow-hidden" data-testid="settings-details-ghl">
          <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between hover:bg-slate-50">
            <div>
              <div className="text-lg text-slate-900 font-semibold flex items-center gap-2">
                <Plugs size={18} className="text-[#0EA5E9]" /> GoHighLevel CRM
              </div>
              <div className="text-xs text-slate-500 mt-0.5">Pipeline and stage mapping for automatic CRM sync.</div>
            </div>
            <CaretDown size={18} className="text-slate-500 transition-transform group-open:rotate-180" />
          </summary>
          <div className="border-t border-slate-200 px-6 py-5">
          <Card className="glass-card border-slate-200">
            <CardContent className="space-y-4 pt-6">
              <div className="flex items-center justify-between gap-3 pb-3 border-b border-slate-200">
                <p className="text-sm text-slate-500">Verify credentials and pipeline access before mapping stages.</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGhlTest}
                  disabled={ghlTesting}
                  className="border-slate-200 text-slate-700 hover:bg-slate-100 flex-shrink-0"
                  data-testid="ghl-test-btn"
                >
                  {ghlTesting ? <ArrowsClockwise size={16} className="animate-spin mr-1" /> : <Plugs size={16} className="mr-1" />}
                  Test Connection
                </Button>
              </div>
              {ghlTestResult && (
                <div className={`p-3 rounded-lg text-sm flex items-center gap-2 ${ghlTestResult.success ? "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20" : "bg-red-500/10 text-red-400 border border-red-500/20"}`}>
                  {ghlTestResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                  {ghlTestResult.message}
                </div>
              )}
              {/* Pipeline Selector */}
              <div className="space-y-2">
                <Label className="text-slate-700">Pipeline</Label>
                <Select value={ghlSettings.pipeline_id} onValueChange={handleGhlPipelineChange}>
                  <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900" data-testid="ghl-pipeline-select">
                    <SelectValue placeholder={ghlLoadingPipelines ? "Loading pipelines..." : "Select pipeline"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    {ghlPipelines.map(p => (
                      <SelectItem key={p.id} value={p.id} className="text-slate-900 hover:bg-slate-100">{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Stage Mappings */}
              {ghlSettings.pipeline_id && ghlStages.length > 0 && (
                <div className="space-y-3 p-4 rounded-lg bg-white border border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700">Stage Mapping</h4>
                  <p className="text-xs text-slate-500">Map LeadFlow stages to your GHL pipeline stages</p>

                  {[
                    { key: "new_lead", label: "New Lead", desc: "Initial stage when lead is pushed" },
                    { key: "contacted", label: "Contacted", desc: "First outreach email sent" },
                    { key: "replied", label: "Replied", desc: "Lead replied to email" },
                    { key: "meeting_booked", label: "Meeting Booked", desc: "Meeting has been scheduled" },
                    { key: "closed_lost", label: "Closed Lost", desc: "Bounced or unsubscribed" }
                  ].map(stage => (
                    <div key={stage.key} className="flex items-center gap-3">
                      <div className="w-36 flex-shrink-0">
                        <p className="text-sm text-slate-900">{stage.label}</p>
                        <p className="text-xs text-slate-500">{stage.desc}</p>
                      </div>
                      <Select
                        value={ghlSettings.stage_mapping?.[stage.key] || ""}
                        onValueChange={(v) => handleGhlStageMapping(stage.key, v)}
                      >
                        <SelectTrigger className="bg-white/50 border-slate-200 text-slate-900 text-sm" data-testid={`ghl-stage-${stage.key}`}>
                          <SelectValue placeholder="Select stage..." />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-slate-200">
                          {ghlStages.map(s => (
                            <SelectItem key={s.id} value={s.id} className="text-slate-900 hover:bg-slate-100">{s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              )}

              <Button
                onClick={handleGhlSave}
                disabled={ghlSaving || !ghlSettings.pipeline_id}
                className="w-full bg-[#0EA5E9] hover:bg-[#0284C7] text-slate-900 font-bold rounded-sm "
                data-testid="ghl-save-btn"
              >
                {ghlSaving ? "Saving..." : "Save CRM Settings"}
              </Button>
            </CardContent>
          </Card>
          </div>
         </details>
        </motion.div>
      )}
    </div>
  );
};

export default SettingsPage;
